/**
 * The phone's property MARKS — which glyph a field draws, in which colour,
 * and when a chip must also print its field's name.
 *
 * Moved out of `RecordChipRow` unchanged so a second surface (the office
 * console's review card) draws exactly these marks rather than a copy of
 * them: no motion, no router, no sheet — only React and the mark modules.
 * `RecordChipRow` re-exports everything it used to export from here.
 */
import type { CSSProperties, ReactNode } from "react";
import { CalendarMark, CalendarMarkAt, PriorityMark, StatusMark } from "./PropertyGlyphs";
import type { RecordCardChip } from "../../../shared/recordFields";
import { AssigneeAvatar } from "../shared/assignee/AssigneeAvatar";
import { FieldNameMark, ObservationTypeMark, propertyColor, TradeMark } from "./PropertyAppearance";

export const CHIP_FONT = '"Inter", -apple-system, system-ui, sans-serif';
export const MONO_FONT = '"JetBrains Mono", ui-monospace, "SF Mono", monospace';

// ─── Glyphs ──────────────────────────────────────────────────────────────────

/** Stroke-icon paths on a 24-box, from the design's `GLYPH` table where it has one. */
export const ICON: Record<string, [string, string?]> = {
  pin: [
    "M12 2C8 2 5 5 5 8.8 5 14 12 22 12 22s7-8 7-13.2C19 5 16 2 12 2z",
    "M12 6.4a2.6 2.6 0 100 5.2 2.6 2.6 0 000-5.2z",
  ],
  flag: ["M5 22V3m0 0h12l-2 4 2 4H5"],
  tag: ["M3 12V4h8l10 10-8 8L3 12z", "M7.5 7.5h.01"],
  company: ["M4 21V5l8-3v19M12 21V9l8 3v9", "M2 21h20M7.5 8h1M7.5 12h1M7.5 16h1M15.5 15h1"],
  text: ["M5 7h14M5 12h14M5 17h9"],
  /**
   * Drawn, not typed.
   *
   * The design's MONO table gives these as characters — `time: '\u23F1'`,
   * `weather: '\u2600'` — which is right in a browser, where they render as
   * monochrome text. On iOS both codepoints default to EMOJI presentation, so
   * the phone drew a colour clock and a colour sun in a run of stroke icons.
   * Same shapes, drawn the way every other mark on the card is.
   */
  clock: ["M12 3.4a8.6 8.6 0 100 17.2 8.6 8.6 0 000-17.2", "M12 7.3V12l3.2 2"],
  sun: [
    "M12 8.4a3.6 3.6 0 100 7.2 3.6 3.6 0 000-7.2",
    "M12 2.6v2.1M12 19.3v2.1M4.3 12H2.2M21.8 12h-2.1M6.4 6.4L5 5M19 19l-1.4-1.4M6.4 17.6L5 19M19 5l-1.4 1.4",
  ],
};

/**
 * Single-character marks — the design's `MONO` table, for fields whose meaning
 * is a unit rather than an object.
 */
const MONO: Record<string, string> = {
  // `time` and `sky` were here. Both codepoints emoji-fy on iOS, so they moved
  // to ICON and are drawn. Everything left is a true typographic mark with no
  // emoji presentation to fall into.
  temperature: "°",
  wind: "≋",
  ground: "▤",
  calamity: "!",
};

/** Daily-log field keys → a mark. Anything unlisted falls back on its type. */
const DAILY_LOG_GLYPH: Record<string, string> = {
  time: "clock",
  startTime: "clock",
  endTime: "clock",
  takenAt: "clock",
  inspectionTime: "clock",
  sky: "sun",
  temperature: "temperature",
  wind: "wind",
  ground: "ground",
  calamity: "calamity",
  trade: "trade",
  vendor: "company",
  involvedCompany: "company",
  inspectingEntity: "company",
  deliveryFrom: "company",
  assignee: "person",
  inspectorName: "person",
  involvedName: "person",
  issuedTo: "person",
  issuedBy: "person",
  subject: "person",
  subjectTo: "person",
  subjectFrom: "person",
  status: "flow",
  priority: "bars",
  type: "flag",
  delayType: "tag",
  inspectionType: "tag",
  trackingNumber: "hash",
  ticketNumber: "hash",
  // Named here as well as caught by type in `markFor`: a compact list row asks
  // for a mark with no field type to hand (`RecordPropertyMark`).
  issue: "boolean",
  showed: "boolean",
  inspected: "boolean",
};

/** Compact list rows know these field keys even when no field type is supplied. */
export const NUMERIC_KEYS = new Set(["workers", "hours", "totalHours", "hourlyRate", "quantity", "quantityDelivered", "quantityRemoved", "hoursOperating", "hoursIdle"]);

/** Compare rendered marks when deciding which chips also need a field label. */
export function markName(glyph: string, type: RecordChipFieldType | undefined): string {
  const mark = markFor(glyph, type);
  switch (mark.kind) {
    case "icon":
      return `icon:${mark.d1}`;
    case "mono":
      return `mono:${mark.text}`;
    // A classifier or a switch draws per VALUE, so two of them on one card
    // are two different shapes — but each is still what it is, and the rule
    // below names those whatever they draw.
    default:
      return mark.kind;
  }
}

/**
 * Which chips have to say their field name out loud.
 *
 * The run's rule is that the icon IS the label. It holds while every field on
 * a card has a mark of its own — a flag for priority, a face for a person, a
 * pin for a place — and it fails in two ways:
 *
 * 1. **A mark used twice on one card names neither.** A Delays entry draws
 *    `startTime` and `endTime`, both of which are a clock, so the card shows
 *    two identical "Not set" chips and nothing says which is the start.
 * 2. **A CLASSIFIER cannot be named by a mark at all.** One `tag` serves
 *    `delayType` and `inspectionType`, so it says "this classifies the record"
 *    without saying WHICH classification — and the value then lands a few
 *    chips from the destination and reads as a rival to it. "Daily log DELAYS"
 *    beside a bare "Weather" was read as a contradiction, when it is a Delays
 *    entry whose delay type is Weather.
 * 3. **A CALENDAR names a TYPE, not a field.** Every other mark on the run
 *    names an OBJECT — a pin is the location, a face is the person, a flag is
 *    the priority. A date has no object, only a role, so the calendar says
 *    "this is a date" and leaves due / started / observed to be guessed. It
 *    got away with that while a labelled sheet stood in front of the picker.
 *    The date chip IS iOS's picker now, and iOS's calendar has nowhere to put
 *    a title, so the chip is the only place the field's name can appear at
 *    all — and a reviewer setting a date could not tell you which one.
 * 4. **A SWITCH names a TYPE too.** Notes' Issue, Work Logs' Showed Up and
 *    Equipment's Inspected are all yes/no, and the switch says "yes/no" and
 *    nothing else. A Notes card read "✓ Not set" beside "📍 Not set", and
 *    only the sheet's title said the first one was Issue.
 *
 * Computed over the chips actually on THIS card, so it costs nothing on a
 * punch (every mark distinct) and appears exactly where it is needed.
 */
/**
 * The calendar, as `markName` renders it — resolved once, from the same table
 * the chip draws from, so renaming the icon cannot leave this behind.
 */
export const CALENDAR_MARK = markName("calendar", undefined);

export function fieldsNeedingTheirName(
  chips: ReadonlyArray<RecordCardChip>,
  fieldTypes: Readonly<Record<string, RecordChipFieldType>> | undefined,
): ReadonlySet<string> {
  const seen = new Map<string, number>();
  for (const chip of chips) {
    const name = markName(chip.glyph, fieldTypes?.[chip.key]);
    seen.set(name, (seen.get(name) ?? 0) + 1);
  }
  const need = new Set<string>();
  for (const chip of chips) {
    const name = markName(chip.glyph, fieldTypes?.[chip.key]);
    if (fieldTypes?.[chip.key] === "number" || name === "classifier" || name === "boolean" || name === CALENDAR_MARK || (seen.get(name) ?? 0) > 1) {
      need.add(chip.key);
    }
  }
  return need;
}

/**
 * A field name said out loud inside a chip.
 *
 * ONE typeface per chip. The card's structural labels — the project eyebrow,
 * the stamp, MEDIA — are mono, and that reads as a system while each sits on a
 * line of its own. Inside a 28px pill, a mono label butted against an Inter
 * value reads as two fonts fighting, which is exactly what it looked like.
 * The name is set apart by size, weight and colour instead, and the pill keeps
 * one family.
 */
export const FIELD_NAME_STYLE: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  color: "var(--muted-foreground)",
  flex: "none",
};

export type RecordChipFieldType =
  | "text" | "longText" | "choice" | "multiChoice" | "date" | "number" | "boolean" | "person" | "time";

export type ChipMark =
  | { kind: "numeric"; field: string }
  | { kind: "identifier"; field: string }
  | { kind: "icon"; d1: string; d2?: string }
  | { kind: "mono"; text: string }
  | { kind: "calendar" }
  | { kind: "status" }
  | { kind: "meter" }
  | { kind: "person" }
  | { kind: "observationType" }
  /**
   * Resolved per VALUE against the property icon LEXICON, not a table: a
   * trade vocabulary is the company's own, so the value may be one nothing
   * has ever seen (`shared/propertyIcons.ts`).
   */
  | { kind: "trade" }
  /** Resolved per VALUE, at draw time, against `CLASSIFIER_MARK`. */
  | { kind: "classifier" }
  /** Resolved per VALUE too: one switch per answer (`BooleanMark`). */
  | { kind: "boolean" };

/**
 * `RecordCardChip.glyph` is a semantic name for observation / task / punch
 * (`flow`, `bars`, `person`, …) and the FIELD KEY for daily logs
 * (`recordFields.fromDailyLogField`). One resolver reads both.
 */
export function markFor(glyph: string, type: RecordChipFieldType | undefined, label = ""): ChipMark {
  if (type === "number" || NUMERIC_KEYS.has(glyph)) {
    return { kind: "numeric", field: `${glyph.replace(/([a-z])([A-Z])/g, "$1 $2")} ${label}`.trim() };
  }
  const name = DAILY_LOG_GLYPH[glyph] ?? glyph;
  if (name === "hash") return { kind: "identifier", field: glyph === "hash" ? "reference" : glyph.replace(/([a-z])([A-Z])/g, "$1 $2") };
  if (name === "flow") return { kind: "status" };
  if (name === "bars") return { kind: "meter" };
  if (name === "flag") return { kind: "observationType" };
  // Before ICON, which used to hold a `trade` entry byte-identical to `tag`:
  // one luggage tag for every trade on every record.
  if (name === "trade") return { kind: "trade" };
  // Before ICON, which holds the generic `tag` this falls back to.
  if (name === "tag") return { kind: "classifier" };
  if (name === "boolean" || type === "boolean") return { kind: "boolean" };
  if (name === "person" || type === "person") return { kind: "person" };
  if (MONO[name]) return { kind: "mono", text: MONO[name] };
  if (name === "calendar" || type === "date") return { kind: "calendar" };
  const icon = ICON[name];
  if (icon) return { kind: "icon", d1: icon[0], d2: icon[1] };
  // A clock for any time field, not only the keys the table happens to name.
  if (type === "time") return { kind: "icon", d1: ICON.clock[0], d2: ICON.clock[1] };
  return { kind: "icon", d1: ICON.text[0] };
}

/**
 * One mark per CLASSIFICATION, on the same principle as `StatusMark`: the
 * value is the thing being read, so the value gets the shape.
 *
 * `tag` is the mark for a field that says what kind of record this is —
 * `delayType`, `inspectionType`. Drawn generically it was the same label on
 * every card: seven delay types, one identical luggage tag, so the run told
 * you a delay had a type and made you read the word to learn which. These are
 * the seven `PROCORE_DELAY_TYPE_OPTIONS`, keyed by the value as it renders
 * (Procore stores its own display spelling for this field).
 *
 * A classification with no entry — an inspection type, which is free text off
 * the project's own list — keeps the generic tag. The table is an improvement
 * where it knows the vocabulary, never a requirement.
 */
const CLASSIFIER_MARK: Record<string, ChipMark> = {
  Weather: {
    kind: "icon",
    d1: "M7.6 16.4h8.6a3.7 3.7 0 00.4-7.3 5.3 5.3 0 00-10-1.2 3.8 3.8 0 001 8.5z",
    d2: "M9.1 19.1l-.9 2.3M12.5 19.1l-.9 2.3M15.9 19.1l-.9 2.3",
  },
  Material: {
    kind: "icon",
    d1: "M12 3.2l8 4.2v9.2l-8 4.2-8-4.2V7.4z",
    d2: "M4 7.4l8 4.2 8-4.2M12 11.6v9",
  },
  "Existing Conditions": {
    kind: "icon",
    d1: "M12 3.4L3.4 8 12 12.6 20.6 8z",
    d2: "M3.4 12.5L12 17.1l8.6-4.6M3.4 16.7L12 21.3l8.6-4.6",
  },
  "Owner Directive": {
    kind: "icon",
    d1: "M6 3.4h8.2L19 8.2V20a.6.6 0 01-.6.6H6a.6.6 0 01-.6-.6V4a.6.6 0 01.6-.6z",
    d2: "M14 3.6v4.8h4.8M8.4 12.6h7.2M8.4 16.2h4.8",
  },
  "Safety Event": {
    kind: "icon",
    d1: "M12 2.9l7 2.7v5.3c0 4.5-2.9 7.9-7 10.2-4.1-2.3-7-5.7-7-10.2V5.6z",
    d2: "M12 8.2v4.4M12 15.9h.01",
  },
  // Typographic, like the rest of MONO: a "not equal to" IS the field's
  // meaning, and no drawn shape says it as plainly at 13px.
  "Non-Conformance": { kind: "mono", text: "≠" },
  // U+2605, which has no emoji presentation — unlike U+2B50, and unlike the
  // clock and sun this file had to stop typing (see ICON).
  Quality: { kind: "mono", text: "★" },
};

/**
 * A yes/no field's answer, from whichever spelling reached the chip.
 *
 * Two hosts spell the same answer differently. The review deck's details
 * formatter writes "Yes" / "No"; `getRecordCardChips` writes the FIELD'S OWN
 * LABEL for true ("Issue") and drops false. An Autodesk toggle arrives as
 * "Yes" / "No" text. All of them mean one of three things, and the mark and
 * the word below are drawn from that, never from the raw string.
 */
export function readBoolean(value: string): "yes" | "no" | null {
  const v = value.trim();
  if (!v) return null;
  return /^(no|false|0)$/i.test(v) ? "no" : "yes";
}

export const BOOLEAN_WORD = { yes: "Yes", no: "No" } as const;

/**
 * One SWITCH per answer, so Issue / Showed Up / Inspected scan by shape the
 * way a status does: solid on, outlined off, dashed for "nobody said".
 *
 * A switch because every nearer shape is spoken for. The check this replaced
 * is the task's own tick; a ticked, struck or dashed RING is a status
 * (`StatusMark`); the warning triangle is the punch item. A switch is drawn
 * nowhere else on a card, and it is what Settings already uses for yes/no.
 *
 * Same 24-box and stroke as `ICON`. "Yes" is FILLED rather than stroked: the
 * box is the stroked track's outer edge, and the knob is a hole in it
 * (even-odd), so it reads at 12.5px and shows the chip through, light or dark.
 * Stroke and fill are never laid on the same path.
 */
const SWITCH_TRACK = "M8.4 5.9h7.2a6.1 6.1 0 010 12.2H8.4a6.1 6.1 0 010-12.2z";
const SWITCH_ON =
  "M8.4 5.05h7.2a6.95 6.95 0 010 13.9H8.4a6.95 6.95 0 010-13.9zM15.6 8.6a3.4 3.4 0 100 6.8 3.4 3.4 0 000-6.8z";

function BooleanMark({ value, fieldKey }: { value: string; fieldKey?: string }) {
  const answer = readBoolean(value);
  // No answer is drawn in the muted ink, like every other blank mark. In the
  // field's own colour the empty track was the one unset mark on a card that
  // still looked answered — a saturated violet pill in a row of grey.
  const color = answer === "yes" ? (fieldKey === "issue" ? "var(--signal-amber)" : "var(--signal-green)") : answer === "no" ? (fieldKey === "issue" ? "var(--signal-green)" : "var(--signal-amber)") : "var(--muted-foreground)";
  return (
    <svg data-boolean-mark={answer ?? "unset"} width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
      style={{ flex: "none" }}>
      {answer === "yes" ? (
        <path d={SWITCH_ON} fill={color} fillRule="evenodd" stroke="none" />
      ) : answer === "no" ? (
        <>
          <path d={SWITCH_TRACK} />
          <circle cx="8.4" cy="12" r="2.6" />
        </>
      ) : (
        <path d={SWITCH_TRACK} strokeDasharray="2.6 2.6" />
      )}
    </svg>
  );
}

/** High / urgent carry the red dot: the queue never hides a defect behind a tone. */
export function isAlarmingLevel(value: string): boolean {
  const v = value.trim().toLowerCase();
  return v === "high" || v === "urgent";
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Kinds that already DRAW their own empty state, and so must be left alone:
 * the status ring loses its core, the priority meter lights no bar, the
 * boolean track empties, a trade and an observation type fall back to a muted
 * placeholder, a calendar drops its header band and its day marks, and a
 * person is an outline where a name would be a filled tile of initials. Each
 * is unmistakable without help, and the dash below would only muddy it.
 *
 * They all still MUTE — looking unset and being distinguishable are two
 * different jobs, and only the second one is this set's business.
 */
const DRAWS_ITS_OWN_UNSET: ReadonlySet<ChipMark["kind"]> = new Set([
  "status", "meter", "boolean", "trade", "observationType", "calendar", "person",
]);

/**
 * A property's mark, and the fallback for a field whose mark had no empty
 * state of its own — one that draws the SAME shape either way: a plain icon,
 * a monogram, a classification tag whose value it does not know.
 *
 * Those keep their own shape — the condensed bar shows no labels, so the
 * shape is the only thing that still says WHICH field is missing — and are
 * drawn dashed in the muted ink (`.record-property-unset`), the vocabulary
 * `StatusMark` and `BooleanMark` already used for a value nobody had set.
 *
 * It is a fallback and not a rule. A mark whose empty state is ALREADY unique
 * says it better than a dashed copy of its filled self, and dashing it on top
 * blurs a glyph that was already clear — so the dash is for the marks that
 * would otherwise be identical, and nothing else (`unsetMarks.test.tsx`).
 *
 * Applied HERE because this is the chokepoint: the chip run, the condensed
 * bar, a compact list row and a field sheet's rows all draw through it.
 */
/**
 * `now` draws a date against that instant instead of the record clock. Pass it
 * from a surface that renders these marks with its own copy of React (the
 * office console), where the clock's hook cannot run.
 */
export function Mark(props: { mark: ChipMark; value: string; fieldKey?: string; dateValue?: string | null; now?: number }) {
  if (props.value.trim() || DRAWS_ITS_OWN_UNSET.has(props.mark.kind)) return <MarkBody {...props} />;
  return (
    <span className="record-property-unset" data-property-unset="true">
      <MarkBody {...props} />
    </span>
  );
}

function MarkBody({ mark, value, fieldKey, dateValue, now }: { mark: ChipMark; value: string; fieldKey?: string; dateValue?: string | null; now?: number }) {
  const color = propertyColor(fieldKey, mark.kind === "mono" ? "hash" : mark.kind === "icon" && mark.d1 === ICON.clock[0] ? "clock" : "");
  switch (mark.kind) {
    case "numeric":
    case "identifier":
      return <FieldNameMark field={mark.field} empty={!value.trim()} />;
    case "observationType":
      return <ObservationTypeMark value={value} />;
    case "trade":
      return <TradeMark value={value} />;
    case "classifier": {
      const known = CLASSIFIER_MARK[value.trim()];
      return <MarkBody mark={known ?? { kind: "icon", d1: ICON.tag[0], d2: ICON.tag[1] }} value={value} fieldKey={fieldKey} now={now} />;
    }
    case "boolean":
      return <BooleanMark value={value} fieldKey={fieldKey} />;
    case "calendar":
      // `value` is the ground truth for empty, not `dateValue`: a chip under an
      // open iOS date picker keeps showing the date it had.
      return now === undefined
        ? <CalendarMark dateValue={dateValue} empty={!value.trim()} due={fieldKey === "dueDate" || fieldKey === "complianceDue"} />
        : <CalendarMarkAt now={now} dateValue={dateValue} empty={!value.trim()} due={fieldKey === "dueDate" || fieldKey === "complianceDue"} />;
    case "status":
      return <StatusMark value={value} />;
    case "meter":
      return <PriorityMark value={value} />;
    case "person": {
      // An EMPTY person is not a person called "?": `initialsOf` returns that
      // for a blank, and on a review card — where every editable field draws
      // a chip, filled or not — it put a question mark where a face goes.
      //
      // This outline IS the field's empty state: a name becomes a filled tile
      // of initials, so the two can never be mistaken and the dash has nothing
      // to add. It only has to be MUTED — in the field's own blue it was the
      // unset mark that started all of this, reading as somebody assigned.
      if (!value.trim()) {
        return (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--muted-foreground)"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flex: "none" }}>
            <circle cx="12" cy="8" r="3.6" />
            <path d="M5 20c0-3.6 3.1-6.4 7-6.4s7 2.8 7 6.4" />
          </svg>
        );
      }
      return <AssigneeAvatar name={value} size={20} />;
    }
    case "mono":
      return (
        <span aria-hidden style={{
          fontFamily: MONO_FONT, fontSize: 13, fontWeight: 600, flex: "none",
          // DECIDED HERE, not in `.record-property-unset`. That rule restyles a
          // mark by beating an SVG presentation attribute, and a typographic
          // mark has none — it has an inline colour, which beats the rule
          // instead. An unset ° stayed orange and only lost a little opacity.
          color: value.trim() ? color : "var(--muted-foreground)",
        }}>
          {mark.text}
        </span>
      );
    case "icon":
      return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flex: "none" }}>
          <path d={mark.d1} />
          {mark.d2 ? <path d={mark.d2} /> : null}
        </svg>
      );
  }
}

/** Static property mark for compact record lists; shares the card’s vocabulary. */
export function RecordPropertyMark({ glyph, value }: { glyph: string; value: string }) {
  return <Mark mark={markFor(glyph, undefined)} value={value} fieldKey={glyph} />;
}

/** What a picker draws beside one of its options. See `valueMarksFor`. */
export interface ChoiceValueMarks {
  /** The mark, drawn before the option's label. */
  mark: (value: string) => ReactNode;
  /** The chip's red dot — High / Urgent on a priority. */
  alarming: (value: string) => boolean;
}

/**
 * The marks a field's PICKER has to draw — or `null` when it has none to draw.
 *
 * A chip whose mark changes with its value (a status ring, the priority bars,
 * a delay type, a yes/no switch) is read by that mark first and its word
 * second. Its sheet listed the same values as bare words, so the reviewer
 * chose "Medium" from a list and got two lit bars on the card — a mark that
 * appeared nowhere in what they had picked from (LED-680).
 *
 * So the sheet draws each option with the mark the chip WILL draw once it is
 * chosen: same resolver, same `Mark`, keyed by the same rendered label. A
 * field whose mark is the same for every value (a calendar, a pin, a `#`)
 * returns `null`: one identical glyph on every row would say nothing the
 * sheet's title does not.
 *
 * A person is not here: the assignee picker is its own sheet and draws its
 * own faces.
 */
export function valueMarksFor(glyph: string, type: RecordChipFieldType | undefined): ChoiceValueMarks | null {
  const mark = markFor(glyph, type);
  switch (mark.kind) {
    case "status":
    case "meter":
    case "classifier":
    case "boolean":
    case "observationType":
    case "trade":
      return {
        mark: (value) => <Mark mark={mark} value={value} fieldKey={glyph} />,
        alarming: (value) => mark.kind === "meter" && isAlarmingLevel(value),
      };
    default:
      return null;
  }
}

